/** venture/VicsDocs.tsx — VICS 화면 위쪽 「서류」 띠: 서류마다 한 줄 — 있으면 보기·인식·다운로드·다시, 없으면 업로드·발급 사이트(+ID·PW) (2026-09-24 UI 정리)
 *  값 행에는 📄 버튼만 남기고 업로드·링크·인식은 여기 한 곳에 모은다 (지도사: "업로드·링크·복사 버튼이 쭉 나열되어 어색").
 *  자동 인식: 글자 PDF(인터넷등기소·벤처확인시스템·홈택스 출력본)는 즉시, 스캔·사진은 AI — 빈 칸만 채우고 「서류 인식」 표식,
 *  이미 값이 있고 다르면 「바꾸기」로 고르게 한다. */
import React, { useState } from 'react';
import { mutStore, UpLabel, renameUpload, dispName } from './stepcommon';
import { s3upload, uploadPreview, vicsExtract, fileUrl } from './api';
import type { VicsExtractResult } from './api';
import { downloadUploadFile, downloadCollectFile } from './docgen';
import { coUploadId } from './collect';
import { vicsDoc, vicsSlot, applyExtract, vcertCred, VICS_DOC_SITES, EXTRACT_DOCS } from './vics13';
import type { VicsStage, VicsDoc, VicsDocKey, ExtractDoc, ExtractApply } from './vics13';
import type { Project, CoCard } from './types';

interface Props { p: Project; co: CoCard; stage: VicsStage; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; onView: (key: VicsDocKey) => void; }
interface Result extends ExtractApply { doc: ExtractDoc; label: string; source: VicsExtractResult['source']; warnings: string[]; }

const SRC_KO: Record<string, string> = { text: '글자 PDF', ai: 'AI 가 읽음', 'text+ai': '글자 + AI', none: '읽지 못함' };
const SHORT: Record<string, string> = { regcb: '등기부등본', biz: '사업자등록증', vcert: '벤처기업확인서', fundout: '입금내역' };
export const isExtractDoc = (k: VicsDocKey): k is ExtractDoc => (EXTRACT_DOCS as string[]).indexOf(k) >= 0;
const copy = async (text: string, toast: (m: string) => void, ok: string) => { try { await navigator.clipboard.writeText(text); toast(ok); } catch { toast('복사 실패 — 직접 선택해 복사해 주세요'); } };

/** 미리보기 URL — 단계 업로드(S3) 또는 1단계 자료수집 제출물(uploadId). 없으면 null */
export function docLoader(p: Project, d: VicsDoc): (() => Promise<string>) | null {
  if (!d.name) return null;
  if (d.s3key) return () => fileUrl(d.s3key, d.name, true);
  if (d.collect && p.collect) { const uid = coUploadId(p.collect, d.name); if (uid) return () => uploadPreview(uid).then((r) => r.url); }
  return null;
}

/** 벤처확인시스템 로그인 ID·PW — 9단계 값을 보여 주고, 없으면 여기서 바로 적는다(9단계 칸에 저장 · 「직접 입력」으로 표시) */
export function CredChip({ p, co, toast, onPatch }: { p: Project; co: CoCard; toast: (m: string) => void; onPatch: (patch: Partial<Project>) => void }) {
  const c = vcertCred(p, co);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(!c.text);   // 없으면 바로 입력칸 — 「완료」 누를 때까지 유지 (한 글자 넣자마자 표시 모드로 바뀌지 않게)
  const [id, setId] = useState(c.id || (c.text && !c.pw ? c.text : ''));
  const [pw, setPw] = useState(c.pw);
  const key = co.id + '_idpw';
  const save = (nid: string, npw: string) => {
    setId(nid); setPw(npw);
    onPatch(mutStore(p, 's9', (st) => {
      st.txt = st.txt || {}; st.src = st.src || {};
      const v = (nid.trim() && npw.trim()) ? nid.trim() + ' / ' + npw.trim() : (nid.trim() || npw.trim());
      if (v) st.txt[key] = v; else delete st.txt[key];
      st.src[key] = 'upload';   // 9단계 행도 「직접 입력」으로 표시
    }));
  };
  if (edit) {
    return (
      <span className="vcred">
        <span className="vitem"><span className="vl">ID</span><input className="cotext" style={{ width: 130 }} placeholder="벤처확인시스템 ID" value={id} onChange={(e) => save(e.target.value, pw)} /></span>
        <span className="vitem"><span className="vl">PW</span><input className="cotext" style={{ width: 130 }} placeholder="비밀번호" value={pw} onChange={(e) => save(id, e.target.value)} /></span>
        <button type="button" className="rbtn" disabled={!id.trim() || !pw.trim()} onClick={() => setEdit(false)}>완료</button>
        <span className="s3muted">여기 적으면 9단계 칸에 저장됩니다</span>
      </span>
    );
  }
  if (!c.text) return <span className="vcred"><span className="s3muted">ID·PW 없음 (9단계)</span><button type="button" className="rbtn ghost" onClick={() => setEdit(true)}>여기서 입력</button></span>;
  if (!c.id) return <span className="vcred"><span className="vl">ID·PW</span><code>{c.text}</code><button type="button" className="rbtn" onClick={() => copy(c.text, toast, 'ID·PW를 복사했습니다')}>복사</button><span className="s3muted">{c.from}</span><button type="button" className="rbtn ghost" onClick={() => { setId(c.text); setPw(''); setEdit(true); }}>수정</button></span>;
  return (
    <span className="vcred">
      <span className="vl">ID</span><code>{c.id}</code><button type="button" className="rbtn" onClick={() => copy(c.id, toast, 'ID를 복사했습니다')}>복사</button>
      <span className="vl">PW</span><code>{show ? c.pw : '•'.repeat(Math.min(10, Math.max(6, c.pw.length)))}</code>
      <button type="button" className="rbtn" title={show ? '가리기' : '보기'} onClick={() => setShow(!show)}>{show ? '가림' : '👁'}</button>
      <button type="button" className="rbtn" onClick={() => copy(c.pw, toast, 'PW를 복사했습니다')}>복사</button>
      <span className="s3muted">{c.from}</span>
      {c.from === '9단계 입력' ? <button type="button" className="rbtn ghost" onClick={() => { setId(c.id); setPw(c.pw); setEdit(true); }}>수정</button> : null}
    </span>
  );
}

export default function VicsDocs({ p, co, stage, onPatch, toast, onView }: Props) {
  const [busy, setBusy] = useState<string>('');
  const [results, setResults] = useState<Result[]>([]);
  const docs = stage.docs.map((k) => vicsDoc(p, co, k));
  const owner = co.name || '법인';
  // 다운로드·저장 파일명은 항상 「서류명_법인명.확장자」 (지도사 2026-09-24: "다운로드하면 항상 파일명 정돈")
  const dlDoc = (d: VicsDoc) => (d.collect ? downloadCollectFile(p, d.name, toast) : downloadUploadFile(p, d.s3key, dispName(d.name, SHORT[d.key] || d.label, owner), toast));
  const upDoc = async (d: VicsDoc, f0: File) => {
    try {
      const f = renameUpload(f0, SHORT[d.key] || d.label, owner);
      const r = await s3upload(p.id, f);
      // 앞단계 칸(8·9·12단계)에 저장 — 앞단계 행에 「1단계 연동/직접 업로드」 선택이 있으면 「직접 업로드」로 바꿔 바로 보이게
      onPatch(mutStore(p, d.store, (st) => { st.up[d.slot] = r.name; st.upKey = st.upKey || {}; st.upKey[d.slot] = r.key; if (d.srcKey) { st.src = st.src || {}; st.src[d.srcKey] = 'upload'; } }));
      toast('업로드됨 · ' + r.name + ' — ' + d.from.replace(/\(구\)|연동/g, '').trim() + ' 칸에도 바로 등록 · 「✨ 자동 채우기」로 값을 읽어 올 수 있습니다');
    } catch (e) { toast((e as Error).message); }
  };
  const extractOne = async (d: VicsDoc, base: Project): Promise<Project> => {
    const key = d.key;
    if (!isExtractDoc(key)) return base;
    const uid = !d.s3key && d.collect && p.collect ? coUploadId(p.collect, d.name) : '';
    if (!d.s3key && !uid) { toast(d.label + ' 파일 위치를 찾지 못했습니다'); return base; }
    const r = await vicsExtract(p.id, { doc: key, s3key: d.s3key || undefined, uploadId: uid || undefined, name: d.name });
    const ap = applyExtract(base, co, key, r.fields);
    const row: Result = { ...ap, doc: key, label: d.label, source: r.source, warnings: r.warnings || [] };
    setResults((rs) => rs.filter((x) => x.doc !== key).concat([row]));
    return Object.keys(ap.patch).length ? { ...base, ...ap.patch } : base;
  };
  const run = async (list: VicsDoc[]) => {
    if (busy) return;
    setBusy(list.length > 1 ? 'all' : list[0].key);
    try {
      let cur: Project = p; let n = 0;
      for (let i = 0; i < list.length; i++) { const before = cur; cur = await extractOne(list[i], cur); if (cur !== before) n += 1; }
      if (cur !== p) onPatch({ s13: cur.s13 });
      toast(n ? '서류 ' + list.length + '건을 읽었습니다 — 아래에서 채운 값을 확인하세요' : '새로 채울 값이 없습니다 (이미 같은 값이거나 읽지 못함)');
    } catch (e) { toast('읽기 실패: ' + (e as Error).message); } finally { setBusy(''); }
  };
  const undo = (slot: string) => onPatch(mutStore(p, 's13', (st) => { if (st.txt) delete st.txt[vicsSlot(co.id, slot)]; if (st.src) delete st.src[vicsSlot(co.id, slot)]; }));
  const replace = (r: Result, slot: string, value: string) => {
    onPatch(mutStore(p, 's13', (st) => { st.txt = st.txt || {}; st.src = st.src || {}; st.txt[vicsSlot(co.id, slot)] = value; st.src[vicsSlot(co.id, slot)] = 'doc:' + r.doc; }));
    setResults((rs) => rs.map((x) => (x.doc === r.doc ? { ...x, conflicts: x.conflicts.filter((c) => c.slot !== slot), filled: x.filled.concat(x.conflicts.filter((c) => c.slot === slot)) } : x)));
  };
  const present = docs.filter((d) => d.name && isExtractDoc(d.key));
  const missing = docs.filter((d) => !d.name).length;

  return (
    <div className="vdocs" id={'vdocs-' + co.id + '-' + stage.key}>
      <div className="vdocs-h">
        <b>서류</b>
        <span className="s3muted">{missing ? '없는 서류 ' + missing + '건 — 여기서 올리거나 사이트에서 발급받으세요' : '모두 준비됨 — 「보기」는 보면서 입력, 「자동 채우기」는 서류를 읽어 빈 칸을 채웁니다'}</span>
        <span className="grow" />
        {present.length > 1 ? <button type="button" className="rbtn ok" disabled={!!busy} title="서류 전부를 읽어 이 화면의 빈 칸을 한 번에 채웁니다" onClick={() => run(present)}>{busy === 'all' ? '읽는 중…' : '✨ 서류 ' + present.length + '건 자동 채우기'}</button> : null}
      </div>
      {docs.map((d) => {
        const site = VICS_DOC_SITES[d.key];
        return (
          <div className={'vdoc' + (d.name ? ' has' : '')} key={d.key}>
            <span className="vdoc-l">📄 {SHORT[d.key] || d.label}</span>
            <span className="vdoc-m">{d.name
              ? <><span className="fn" title={d.name}>{d.name}</span><span className="pill p-blue">{d.from}</span></>
              : <span className="pill p-gray">없음</span>}</span>
            <span className="vdoc-a">
              {d.name ? (<>
                <button type="button" className="rbtn" title="서류를 보면서 값을 입력합니다" onClick={() => onView(d.key)}>👁 보기</button>
                {isExtractDoc(d.key) ? <button type="button" className="rbtn ok" disabled={!!busy} title="서류를 읽어 이 화면의 빈 칸을 채웁니다 (글자 PDF 는 바로 · 스캔·사진은 AI)" onClick={() => run([d])}>{busy === d.key ? '읽는 중…' : '✨ 자동 채우기'}</button> : null}
                <button type="button" className="rbtn" title="파일 다운로드" onClick={() => dlDoc(d)}>⬇</button>
                <span className="up-lite" title="다른 파일로 바꿔 올리기"><UpLabel text="교체" onUp={(f) => upDoc(d, f)} /></span>
              </>) : (<>
                <span title={'올린 파일은 ' + d.from + ' 칸에도 같이 보입니다'}><UpLabel text="⬆ 업로드" onUp={(f) => upDoc(d, f)} /></span>
                {site ? <a className="upbtn link" href={site.url} target="_blank" rel="noopener noreferrer" title={site.hint + ' — ' + site.url} style={{ fontSize: 10.5, padding: '3px 8px' }}>🔗 {site.label}</a> : null}
              </>)}
            </span>
            {d.key === 'vcert' ? <span className="vdoc-c"><CredChip p={p} co={co} toast={toast} onPatch={onPatch} /></span> : null}
          </div>
        );
      })}
      {results.map((r) => (
        <div className={'vxres' + (r.filled.length ? ' ok' : '')} key={r.doc}>
          <div className="vxres-h"><b>✨ {SHORT[r.doc] || r.label}에서 읽은 값</b><span className="pill p-blue">{SRC_KO[r.source] || r.source}</span>
            <span className="s3muted">채움 {r.filled.length} · 같음 {r.same} · 다름 {r.conflicts.length}</span><span className="grow" />
            <button type="button" className="rbtn" onClick={() => setResults((rs) => rs.filter((x) => x.doc !== r.doc))}>닫기</button></div>
          {r.filled.map((x) => <div className="vxrow" key={x.slot}><span className="pill p-gray">{x.stage}</span><span className="vl">{x.label}</span><code>{x.value}</code><span className="grow" /><button type="button" className="rbtn" title="채운 값 지우기" onClick={() => undo(x.slot)}>↺</button></div>)}
          {r.conflicts.map((x) => <div className="vxrow warn" key={x.slot}><span className="pill p-amber">{x.stage} 다름</span><span className="vl">{x.label}</span><span className="s3muted">현재 <code>{x.current}</code></span><span>→</span><code>{x.value}</code><span className="grow" /><button type="button" className="rbtn ok" onClick={() => replace(r, x.slot, x.value)}>바꾸기</button></div>)}
          {r.warnings.map((wmsg, i) => <div className="snote" key={i}>⚠ {wmsg}</div>)}
          {!r.filled.length && !r.conflicts.length && !r.warnings.length ? <div className="snote">이미 같은 값이 들어 있어 바꿀 것이 없습니다</div> : null}
        </div>
      ))}
    </div>
  );
}
